import React, { useState } from "react";
import type { ChatSummary } from "./types";
import { Button } from "../../../components/tailwind";
import { ChatDeleteDialog } from "./ChatDeleteDialog";

type ChatSelectMenuProps = {
  summaries: ChatSummary[];
  onChatSelect: (chatId: number) => void;
  onChatCreate: () => void;
  onChatDelete: (chatId: number) => void;
  activeChatId?: number;
};

export const ChatSelectMenu: React.FC<ChatSelectMenuProps> = ({
  summaries,
  onChatSelect,
  onChatCreate,
  onChatDelete,
  activeChatId,
}) => {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [deleteDialogChatId, setDeleteDialogChatId] = useState<number | null>(
    null
  );

  const handleChatDeleteClick = (
    e: React.MouseEvent,
    chatSummary: ChatSummary
  ): void => {
    e.stopPropagation();
    onChatSelect(chatSummary.id);
    setDeleteDialogChatId(chatSummary.id);
  };

  const handleDeleteConfirm = (chatId: number): void => {
    onChatDelete(chatId);
    setDeleteDialogChatId(null);
  };

  const renderChatLink = (
    chatSummary: ChatSummary,
    index: number
  ): React.JSX.Element => {
    const isDeleteModalOpen = deleteDialogChatId === chatSummary.id;

    return (
      <li key={chatSummary.id} className="tw-my-4">
        <div
          className={index > 0 ? "tw-border-t tw-border-gray-200 tw-pt-4" : ""}
        >
          <div
            onClick={(e) => {
              e.stopPropagation();
              onChatSelect(chatSummary.id);
              setDeleteDialogChatId(null);
            }}
            className={`txu-btn tw-h-[8rem] tw-flex tw-flex-col tw-justify-between tw-relative tw-p-4 ${
              activeChatId === chatSummary.id
                ? "tw-bg-gray-200 hover:!tw-shadow-none hover:!tw-brightness-100"
                : ""
            }`}
          >
            {!isDeleteModalOpen ? (
              <>
                <h3 className="tw-text-base tw-m-0">{chatSummary.name}</h3>

                <Button
                  onClick={(e) => handleChatDeleteClick(e, chatSummary)}
                  compact="very"
                  className="tw-absolute tw-top-2 tw-right-2 !tw-px-1 !tw-bg-transparent"
                  borderless
                  variant="secondary"
                  data-balloon="Delete Chat"
                  data-balloon-pos="right"
                  icon
                >
                  <i className="material-icons tw-text-xl tw-text-gray-500">
                    delete
                  </i>
                </Button>
              </>
            ) : (
              <ChatDeleteDialog
                open={isDeleteModalOpen}
                setOpen={(open) =>
                  setDeleteDialogChatId(open ? chatSummary.id : null)
                }
                confirmAction={() => handleDeleteConfirm(chatSummary.id)}
                header={<>Delete chat</>}
              />
            )}
            <span className="tw-text-sm tw-text-gray-600">
              {chatSummary.timeSinceActive || "New"}
            </span>
          </div>
        </div>
      </li>
    );
  };

  return (
    <nav>
      <div className="tw-relative tw-flex tw-flex-col tw-items-start tw-gap-2 -tw-mt-1 tw-mb-4 -tw-ml-1">
        <button
          className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-text-secondary"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          data-balloon="Toggle Sidebar"
          data-balloon-pos="up-left"
        >
          <i className="material-icons tw-text-2xl">auto_awesome_mosaic</i>
          {sidebarOpen && <span>Chat history</span>}
        </button>
        {!sidebarOpen && (
          <button
            className="tw-rounded-lg tw-text-secondary"
            onClick={onChatCreate}
            data-balloon="Start New Conversation"
            data-balloon-pos="down-left"
          >
            <i className="material-icons tw-text-2xl">add</i>
          </button>
        )}
      </div>
      {sidebarOpen && (
        <ul className="tw-w-[20rem]">
          <Button
            key="first"
            variant="primary"
            className="tw-w-full"
            onClick={onChatCreate}
          >
            <div className="tw-flex tw-justify-center tw-items-center tw-gap-2">
              <span>New chat</span> <i className="material-icons ">add</i>
            </div>
          </Button>

          {summaries.map((summary, index) => renderChatLink(summary, index))}
        </ul>
      )}
    </nav>
  );
};
